import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Chip, CircularProgress, Alert, Divider,
  List, ListItemButton, ListItemText, ListItemIcon,
  FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { apiService } from '../../../services/apiService';

const idOf = (v) => (v && typeof v === 'object' ? v._id : v);

/**
 * "Use Template" picker for the observation form. Mirrors the Target Library
 * selector pattern: pick an observatory, then a project, then a saved
 * ObservingTemplate; on confirm the template is applied to the form (observatory,
 * project, and its target list + settings).
 */
const UseTemplateDialog = ({ open, onClose, onApply, observatories = [], projects = [] }) => {
  const [observatoryId, setObservatoryId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [search, setSearch] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (observatoryId) params.observatory_id = observatoryId;
      if (projectId) params.project_id = projectId;
      if (search) params.search = search;
      const resp = await apiService.getLibraryTemplates(params);
      setTemplates(resp.data?.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [observatoryId, projectId, search]);

  // (Re)load whenever the dialog opens or a filter changes (debounced for search).
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(loadTemplates, 250);
    return () => clearTimeout(t);
  }, [open, loadTemplates]);

  // Reset transient state when closing.
  const handleClose = () => {
    setSelected(null);
    onClose();
  };

  const handleApply = () => {
    if (!selected) return;
    onApply(selected);
    setSelected(null);
    onClose();
  };

  const filterSummary = (t) => {
    const filters = new Set();
    (t.targets || []).forEach((tg) => (tg.exposure_configs || []).forEach((ec) => ec.filter && filters.add(ec.filter)));
    return Array.from(filters).join(', ');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DashboardCustomizeIcon color="primary" />
          Use a Template
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
          Choose an observatory and project to narrow the list, then pick a template to
          pre-fill this observation.
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Observatory</InputLabel>
          <Select
            label="Observatory"
            value={observatoryId}
            onChange={(e) => { setObservatoryId(e.target.value); setSelected(null); }}
          >
            <MenuItem value=""><em>All observatories</em></MenuItem>
            {observatories.map((o) => (
              <MenuItem key={o._id} value={o._id}>
                {o.name}{o.code ? ` (${o.code})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Project</InputLabel>
          <Select
            label="Project"
            value={projectId}
            onChange={(e) => { setProjectId(e.target.value); setSelected(null); }}
          >
            <MenuItem value=""><em>All projects</em></MenuItem>
            {projects.map((p) => (
              <MenuItem key={p._id} value={p._id}>
                {p.code ? `${p.code} · ` : ''}{p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth size="small" placeholder="Search templates..."
          value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No templates found</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Save templates from an observation or the Target Library to reuse them here
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 320, overflow: 'auto' }}>
            {templates.map((t) => {
              const targetCount = t.targets?.length || 0;
              const filters = filterSummary(t);
              return (
                <ListItemButton
                  key={t._id}
                  selected={selected?._id === t._id}
                  onClick={() => setSelected(t)}
                  sx={{
                    borderRadius: 1, mb: 0.5,
                    border: selected?._id === t._id ? 2 : 1,
                    borderColor: selected?._id === t._id ? 'primary.main' : 'divider',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <DashboardCustomizeIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2">{t.name}</Typography>
                        <Chip size="small" label={`${targetCount} target${targetCount === 1 ? '' : 's'}`} sx={{ height: 20, fontSize: '0.7rem' }} />
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                          {t.observatory_id?.name ? `Obs: ${t.observatory_id.name}` : 'Any observatory'}
                          {t.project_id?.name ? ` · Project: ${t.project_id.name}` : ''}
                        </Typography>
                        {filters && (
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                            Filters: {filters}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}

        {selected && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Selected: {selected.name}</Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Targets:</strong> {(selected.targets || []).map((t) => t.name).filter(Boolean).join(', ') || '—'}
              </Typography>
              {selected.observatory_id?.name && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Observatory:</strong> {selected.observatory_id.name}
                </Typography>
              )}
              {selected.project_id?.name && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Project:</strong> {selected.project_id.name}
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={!selected}>
          Use Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { idOf };
export default UseTemplateDialog;
